<template>
    <div class="data-page data-page-centered">
        <PageHeader title="Minha Conta" subtitle="Dados da sua conta de acesso ao painel" />

        <div class="data-card data-form">
            <div v-if="state.loading.account" class="data-form-loading">
                <ProgressSpinner />
            </div>

            <form v-else class="data-form-grid" @submit.prevent="saveChanges">

                <div class="data-form-section-title">
                    <i class="pi pi-user"></i>
                    <span>Dados de acesso</span>
                </div>

                <div class="form-field">
                    <label for="account-name">Nome</label>
                    <InputText id="account-name" type="text" v-model="state.form.name" autofocus />
                </div>

                <div class="form-field">
                    <label for="account-login">Login</label>
                    <InputText id="account-login" type="text" v-model="state.form.login" />
                </div>

                <div class="data-form-section-title">
                    <i class="pi pi-lock"></i>
                    <span>Senha</span>
                </div>

                <div class="form-field">
                    <label for="account-password">Nova senha</label>
                    <Password inputId="account-password" v-model="state.form.password" :feedback="false" toggleMask
                        placeholder="Deixe em branco para manter a atual" />
                </div>

                <div class="form-field">
                    <label for="account-password-confirmation">Confirmar nova senha</label>
                    <Password inputId="account-password-confirmation" v-model="state.form.passwordConfirmation"
                        :feedback="false" toggleMask />
                </div>

                <div class="data-form-footer">
                    <Button class="p-button-text" type="button" label="Desfazer" :disabled="!pendingChanges.length"
                        @click="resetForm" />
                    <Button type="submit" :loading="state.loading.save" :disabled="!pendingChanges.length"
                        label="Salvar alterações" icon="pi pi-check" iconPos="right" />
                </div>

            </form>
        </div>
    </div>
</template>

<script>
import InputText from 'primevue/inputtext'
import Password from 'primevue/password'
import Button from 'primevue/button'
import ProgressSpinner from 'primevue/progressspinner'
import PageHeader from '../../components/PageHeader.vue'
import services from '../../services'
import { computed, reactive } from 'vue'
import { useStore } from 'vuex'
import useNotificationToast from '../../composables/useNotificationToast'

export default {

    components: { InputText, Password, Button, ProgressSpinner, PageHeader },

    setup() {

        const state = reactive({
            account: {},
            form: {
                name: '',
                login: '',
                password: '',
                passwordConfirmation: '',
            },
            loading: {
                account: false,
                save: false,
            },
        })

        const store = useStore()
        const { success, error } = useNotificationToast()

        loadAccount()

        function loadAccount() {

            const storedUser = store.getters.getUser

            if (!storedUser?.id) {
                error('Não foi possível identificar a sua conta.')
                return
            }

            // a store já tem o usuário do login: preenche na hora e confirma com a API
            fillForm(storedUser)

            state.loading.account = true

            const token = store.getters.getToken

            services.users.getById({ id: storedUser.id, token })
                .then(({ data }) => {
                    fillForm(data)
                })
                .catch(() => {
                    error('Não foi possível carregar os dados da sua conta.')
                })
                .finally(() => {
                    state.loading.account = false
                })

        }

        function fillForm(account) {
            state.account = { ...account }
            resetForm()
        }

        function resetForm() {
            state.form.name = state.account.name || ''
            state.form.login = state.account.login || ''
            state.form.password = ''
            state.form.passwordConfirmation = ''
        }

        const pendingChanges = computed(() => {
            const changes = []

            if (state.form.name.trim() !== (state.account.name || '')) changes.push('name')
            if (state.form.login.trim() !== (state.account.login || '')) changes.push('login')
            // senha em branco significa manter a atual, não é alteração
            if (state.form.password) changes.push('password')

            return changes
        })

        // as mensagens espelham o UpdateUserValidator do backend, para o erro chegar antes do 422
        function invalidFormMessage() {
            const name = state.form.name.trim()
            const login = state.form.login.trim()

            if (name.length < 4 || name.length > 50) return 'O nome precisa ter entre 4 e 50 caracteres.'
            if (login.length < 2 || login.length > 50) return 'O login precisa ter entre 2 e 50 caracteres.'

            if (state.form.password) {
                if (state.form.password.length < 4) return 'A nova senha precisa ter pelo menos 4 caracteres.'
                if (state.form.password !== state.form.passwordConfirmation) return 'A confirmação não confere com a nova senha.'
            }

            return null
        }

        function saveChanges() {

            if (!pendingChanges.value.length) return

            const invalid = invalidFormMessage()
            if (invalid) return error(invalid)

            state.loading.save = true

            const modifiedUser = {
                id: state.account.id,
                name: state.form.name.trim(),
                login: state.form.login.trim(),
                // o validator exige o campo; a própria conta não muda o próprio status
                active: !!state.account.active,
            }

            if (state.form.password) modifiedUser.password = state.form.password

            const token = store.getters.getToken

            services.users.update({ modifiedUser, token })
                .then(({ data }) => {
                    // mantém o nome do menu lateral em dia com o que foi salvo
                    store.dispatch('setUser', { user: data })

                    fillForm(data)
                    success('Conta atualizada com sucesso!')
                })
                .catch(() => {
                    error('Não foi possível atualizar a sua conta.')
                })
                .finally(() => {
                    state.loading.save = false
                })

        }

        return {
            state,
            pendingChanges,
            resetForm,
            saveChanges,
        }
    }
}
</script>
